import React, { useEffect, useRef } from "react";
import { COOKIE_CONSENT_CATEGORIES } from "@/shared/cookies/cookieConsentCategories";
import type { CookieConsentDraft } from "@/shared/cookies/cookieConsent";
import styles from "@/shared/components/privacy/CookiePreferenceCenter.module.css";

type CookiePreferenceCenterProps = {
  isOpen: boolean;
  draft: CookieConsentDraft;
  onChange: (draft: CookieConsentDraft) => void;
  onClose: () => void;
  onSave: () => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
};

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const CookiePreferenceCenter = ({
  isOpen,
  draft,
  onChange,
  onClose,
  onSave,
  onAcceptAll,
  onRejectAll,
}: CookiePreferenceCenterProps) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";

    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable?.[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const activeElements = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!activeElements || activeElements.length === 0) {
        return;
      }

      const first = activeElements[0];
      const last = activeElements[activeElements.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.backdrop} onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className={`card ${styles.dialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-preferences-title"
        aria-describedby="cookie-preferences-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headerCopy}>
            <span className={styles.eyebrow}>Privacy Controls</span>
            <h2 id="cookie-preferences-title">Privacy Preference Center</h2>
            <p id="cookie-preferences-description" className={styles.intro}>
              When you use ConfidaCare, we may store or retrieve information on your
              browser, mostly in the form of cookies. Some cookies are required to keep
              the site secure and support login, care search, provider access, and basic
              platform features. Optional cookies help us improve performance, remember
              preferences, and understand how the platform is used. You can choose which
              optional categories to allow.
            </p>
          </div>
          <button
            type="button"
            className={`btn btn-ghost btn-sm ${styles.closeButton}`}
            onClick={onClose}
            aria-label="Close cookie preferences"
          >
            Close
          </button>
        </div>

        <div className={styles.categories}>
          {COOKIE_CONSENT_CATEGORIES.map((category) => {
            const isRequired = category.isRequired;
            const isChecked = category.key === "necessary" ? true : draft[category.key];

            return (
              <section key={category.key} className={styles.categoryCard}>
                <div className={styles.categoryHeader}>
                  <div>
                    <h3>{category.title}</h3>
                    <p>{category.description}</p>
                  </div>
                  <label className={styles.toggleWrap}>
                    <span className={styles.toggleLabel}>
                      {isRequired ? "Always Active" : isChecked ? "Allowed" : "Off"}
                    </span>
                    <span className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isRequired}
                        aria-label={category.title}
                        onChange={(event) => {
                          if (isRequired) {
                            return;
                          }

                          onChange({
                            ...draft,
                            [category.key]: event.target.checked,
                          });
                        }}
                      />
                      <span className={styles.slider} aria-hidden="true" />
                    </span>
                  </label>
                </div>
              </section>
            );
          })}
        </div>

        <div className={styles.footer}>
          <button type="button" className="btn btn-outline btn-sm" onClick={onRejectAll}>
            Reject All
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onSave}>
            Save My Choices
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={onAcceptAll}>
            Accept All Cookies
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferenceCenter;
